import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import {
  DestinationDiscoveryMapModule,
  PointOfInterest,
  ThingToDo,
  Hotel,
  DestinationDiscoveryMapFlexModuleResult,
} from "typings/microserviceModels/destination-discovery-map-flex-module";
import { DestinationDiscoveryMapProps, MarkerCardContent, MapProps, Position, MarkerType } from "./typings";
import Map from "./Map";
import { UitkEGMapsQualifier } from "@egds/react-core/map";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { updateQueryStringParameter } from "src/components/utility/UrlUtils";
import { observer } from "mobx-react";
import { withStores } from "stores";

export const DestinationDiscoveryMap = withStores(
  "context",
  "flexModuleModelStore"
)(
  observer((props: DestinationDiscoveryMapProps) => {
    const { templateComponent, flexModuleModelStore, context } = props;
    const {
      metadata: { id },
      config,
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as DestinationDiscoveryMapFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const content: DestinationDiscoveryMapModule = model?.content;
    const noContent: boolean = !content || !Object.keys(content).length || model.empty;
    const destination = !noContent && content?.destination?.[0];
    const { formatText } = useLocalization();

    if (noContent || !destination) {
      return null;
    }

    const poiButtonLabel: string =
      model?.poiMapMarkerCardButtonLabel || formatText("destinationDiscoveryMap.button.label.poi");
    const { maxWidthDialog } = model;
    const rfrrId = "TG.LP.Dest.Map";
    const pois = content.poi?.length && content.poi;
    const hotels = content.hotel?.length && content.hotel;
    const activities = content.activity?.length && content.activity;
    const staticMap = content.staticMapUrl;
    const poiMarkers =
      pois &&
      pois.map((poi: PointOfInterest) => {
        const cardContent: MarkerCardContent = {
          id: poi.id,
          type: MarkerType.POI,
          link: poi.link,
          photoUrl: poi.media?.mediaUrl,
          photoAlt: poi.media?.mediaAlt,
          attribution: poi.media && MediaAttribution(poi.media, poi.region.name),
          title: poi.region.name,
          description: poi.region.description,
          buttonLabel: poiButtonLabel,
          buttonIcon: "search",
          rfrr: `${rfrrId}.POI`,
        };
        const position: Position = { lat: poi.latitude, lng: poi.longitude };

        return {
          position,
          cardContent,
          qualifiers: [UitkEGMapsQualifier.POINT_OF_INTEREST],
        };
      });
    const hotelMarkers =
      hotels &&
      hotels.map((hotel: Hotel) => {
        const photoAlt = formatText("destinationDiscoveryMap.hotel.imageAlt", hotel.name);
        const cardContent: MarkerCardContent = {
          id: String(hotel.id),
          type: MarkerType.HOTEL,
          price: hotel.priceWithCurrencySymbol,
          link: hotel.url,
          photoUrl: hotel.images.sizeY,
          photoAlt,
          title: hotel.name,
          description: hotel.description,
          buttonLabel: formatText("destinationDiscoveryMap.button.label.hotel"),
          buttonIcon: "bed",
          rfrr: `${rfrrId}.Hotel`,
        };

        return {
          position: { lat: hotel.coordinates.latitude, lng: hotel.coordinates.longitude },
          cardContent,
          qualifiers: [UitkEGMapsQualifier.HOTEL],
        };
      });
    const activityMarkers =
      activities &&
      activities.map((activity: ThingToDo) => {
        let image = activity.media?.mediaUrlTrimmed;
        if (image) {
          image = updateQueryStringParameter(image, "rw", "320");
          image = updateQueryStringParameter(image, "rh", null);
        }
        const cardContent: MarkerCardContent = {
          id: activity.id,
          type: MarkerType.ACTIVITY,
          price: activity.price,
          link: activity.link,
          photoUrl: image,
          photoAlt: activity.media?.mediaAlt,
          attribution: activity.media && MediaAttribution(activity.media, activity.name),
          title: activity.name,
          description: activity.description,
          buttonLabel: formatText("destinationDiscoveryMap.button.label.activity"),
          buttonIcon: "local_activity",
          rfrr: `${rfrrId}.Activity`,
        };

        return {
          position: { lat: activity.latitude, lng: activity.longitude },
          cardContent,
          qualifiers: [UitkEGMapsQualifier.ACTIVITY],
        };
      });

    const mapProps: Omit<MapProps, "analytics"> = { context, destination, staticMap, maxWidthDialog, markers: [] };

    if (activityMarkers) {
      mapProps.activities = activityMarkers;
      mapProps.markers = [...mapProps.markers, ...activityMarkers];
    }

    if (poiMarkers) {
      mapProps.pois = poiMarkers;
      mapProps.markers = [...mapProps.markers, ...poiMarkers];
    }

    if (hotelMarkers) {
      mapProps.hotels = hotelMarkers;
      mapProps.markers = [...mapProps.markers, ...hotelMarkers];
    }

    return (
      <div
        className="DestinationDiscoveryMap"
        id={id}
        data-fm={config.fmId}
        data-testid="destination-discovery-map-component"
      >
        <Map {...mapProps} />
      </div>
    );
  })
);

export default DestinationDiscoveryMap;
